import React, { useState } from 'react'
import { Check, X, Clock, Send, MessageSquare, AlertCircle } from 'lucide-react'
import { templateAPI } from '../services/api'

const TemplateStageManager = ({ template, onUpdate, showMessage = true }) => {
  const [loading, setLoading] = useState(false)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [message, setMessage] = useState(template.message || '')
  const [selectedStage, setSelectedStage] = useState('')

  const stageConfig = {
    pending: {
      label: 'Pending',
      color: 'bg-gray-100 text-gray-800',
      icon: Clock,
      description: 'Awaiting review'
    },
    submitted: {
      label: 'Submitted',
      color: 'bg-blue-100 text-blue-800',
      icon: Send,
      description: 'Submitted for approval'
    },
    rejected: {
      label: 'Rejected',
      color: 'bg-red-100 text-red-800',
      icon: X,
      description: 'Rejected - needs revision'
    },
    approved: {
      label: 'Approved',
      color: 'bg-green-100 text-green-800',
      icon: Check,
      description: 'Approved and ready'
    }
  }

  const handleStageChange = async (newStage, messageText = '') => {
    try {
      setLoading(true)
      const response = await templateAPI.updateStage(template.id, newStage, messageText)
      
      if (response.data.success) {
        onUpdate && onUpdate(response.data.data)
        setShowMessageModal(false)
        setMessage('')
        setSelectedStage('')
      } else {
        alert('Failed to update template stage')
      }
    } catch (error) {
      console.error('Error updating template stage:', error)
      alert('Failed to update template stage')
    } finally {
      setLoading(false)
    }
  }

  const openMessageModal = (stage) => {
    setSelectedStage(stage)
    setMessage(template.message || '')
    setShowMessageModal(true)
  }

  const currentStage = stageConfig[template.stage] || stageConfig.pending
  const IconComponent = currentStage.icon

  return (
    <div className="space-y-3">
      {/* Current Stage Badge */}
      <div className="flex items-center space-x-2">
        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${currentStage.color}`}>
          <IconComponent className="w-3 h-3 mr-1" />
          {currentStage.label}
        </div>
        {showMessage && template.message && (
          <button
            onClick={() => setShowMessageModal(true)}
            className="text-gray-400 hover:text-gray-600"
            title="View message"
          >
            <MessageSquare className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Stage Action Buttons */}
      <div className="flex flex-wrap gap-2">
        {template.stage !== 'submitted' && (
          <button
            onClick={() => handleStageChange('submitted')}
            disabled={loading}
            className="inline-flex items-center px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            <Send className="w-3 h-3 mr-1" />
            Submit
          </button>
        )}
        
        {template.stage !== 'approved' && (
          <button
            onClick={() => openMessageModal('approved')}
            disabled={loading}
            className="inline-flex items-center px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            <Check className="w-3 h-3 mr-1" />
            Approve
          </button>
        )}
        
        {template.stage !== 'rejected' && (
          <button
            onClick={() => openMessageModal('rejected')}
            disabled={loading}
            className="inline-flex items-center px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
          >
            <X className="w-3 h-3 mr-1" />
            Reject
          </button>
        )}
        
        {template.stage !== 'pending' && (
          <button
            onClick={() => handleStageChange('pending')}
            disabled={loading}
            className="inline-flex items-center px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
          >
            <Clock className="w-3 h-3 mr-1" />
            Reset
          </button>
        )}
      </div>

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">
                {selectedStage ? `${stageConfig[selectedStage]?.label} Template` : 'View Message'}
              </h3>
              <button
                onClick={() => setShowMessageModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {selectedStage && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">
                    {selectedStage === 'rejected' ? 'Reason for rejection:' : 'Approval message:'}
                  </span>
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message for Designer
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={selectedStage === 'rejected' 
                  ? "Please provide feedback on what needs to be changed..." 
                  : "Optional message or feedback..."}
                maxLength={512}
                disabled={!selectedStage}
              />
              <p className="text-xs text-gray-500 mt-1">
                {message.length}/512 characters
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowMessageModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              {selectedStage && (
                <button
                  onClick={() => handleStageChange(selectedStage, message)}
                  disabled={loading}
                  className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                    selectedStage === 'approved' ? 'bg-green-600 hover:bg-green-700' :
                    selectedStage === 'rejected' ? 'bg-red-600 hover:bg-red-700' :
                    'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {loading ? 'Updating...' : `${stageConfig[selectedStage]?.label} Template`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TemplateStageManager
